import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getPartPhotoFetchUrl } from '../utils/photoUrl';

/**
 * Load part photos via authenticated API (img src alone cannot send Bearer token).
 */
export default function PartPhotoImage({
  photoUrl,
  dataUrl,
  alt = 'part',
  sx = {},
  onClick,
  thumb = true,
  emptyLabel = '—'
}) {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let blobUrl = null;
    let cancelled = false;

    const cleanup = () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };

    async function load() {
      setFailed(false);
      setSrc('');

      if (dataUrl) {
        setSrc(dataUrl);
        return;
      }
      if (!photoUrl) return;

      const s = String(photoUrl);
      if (s.startsWith('data:')) {
        setSrc(s);
        return;
      }

      const fetchUrl = getPartPhotoFetchUrl(s);
      if (!fetchUrl) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('erp_auth_token') || '';
        const res = await fetch(fetchUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) throw new Error('Not an image');
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(blobUrl);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [photoUrl, dataUrl]);

  if (loading) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...(thumb ? { width: 48, height: 48 } : { minHeight: 120 }), ...sx }}>
        <CircularProgress size={thumb ? 22 : 36} />
      </Box>
    );
  }

  if (failed || !src) {
    return thumb ? emptyLabel : null;
  }

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      sx={{
        objectFit: thumb ? 'cover' : 'contain',
        borderRadius: 1,
        display: 'block',
        ...(thumb ? {
          width: 48,
          height: 48,
          cursor: onClick ? 'pointer' : 'default',
          border: '1px solid #ddd'
        } : {}),
        ...sx
      }}
    />
  );
}
